import axios from "axios";

const STORAGE_KEY = "mi_app_tenant_slug";
const DEFAULT_TENANT = (import.meta.env.VITE_TENANT_SLUG || "default").trim() || "default";
const API_BASE = String(import.meta.env.VITE_API_URL || "").replace(/\/+$/, "");

function normalizeTenantSlug(value) {
  return String(value || "")
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 60);
}

function tenantFromHostname() {
  try {
    const hostname = String(window.location.hostname || "").toLowerCase();
    if (hostname === "localhost") return "";
    const isLocalhost = hostname.endsWith(".localhost");
    const isIp = /^\d{1,3}(\.\d{1,3}){3}$/.test(hostname);
    if (!hostname || isIp) return "";

    const parts = hostname.split(".").filter(Boolean);
    if (isLocalhost && parts.length < 2) return "";
    if (!isLocalhost && parts.length < 3) return "";

    const subdomain = parts[0];
    if (["www", "app", "admin", "pruebas", "staging"].includes(subdomain)) return "";
    return normalizeTenantSlug(subdomain);
  } catch {
    return "";
  }
}

export function getTenantSlug() {
  try {
    const params = new URLSearchParams(window.location.search || "");
    const fromUrl = normalizeTenantSlug(params.get("tenant") || params.get("empresa"));
    if (fromUrl) {
      window.localStorage?.setItem(STORAGE_KEY, fromUrl);
      return fromUrl;
    }
    const fromHost = tenantFromHostname();
    if (fromHost) {
      window.localStorage?.setItem(STORAGE_KEY, fromHost);
      return fromHost;
    }
    const stored = normalizeTenantSlug(window.localStorage?.getItem(STORAGE_KEY));
    if (stored === "localhost") {
      window.localStorage?.removeItem(STORAGE_KEY);
      return normalizeTenantSlug(DEFAULT_TENANT) || "default";
    }
    return stored || normalizeTenantSlug(DEFAULT_TENANT) || "default";
  } catch {
    return normalizeTenantSlug(DEFAULT_TENANT) || "default";
  }
}

export function setTenantSlug(value) {
  const slug = normalizeTenantSlug(value) || "default";
  try {
    window.localStorage?.setItem(STORAGE_KEY, slug);
  } catch {
    // localStorage puede no estar disponible en algun entorno embebido.
  }
  return slug;
}

export function tenantHeaders() {
  const slug = getTenantSlug();
  return {
    "x-tenant": slug,
    "x-tenant-slug": slug,
  };
}

function shouldAttachTenant(input) {
  const rawUrl = typeof input === "string" || input instanceof URL ? String(input) : String(input?.url || "");
  if (!rawUrl) return true;
  if (rawUrl.startsWith("/")) return true;
  try {
    const url = new URL(rawUrl, window.location.origin);
    if (url.origin === window.location.origin) return true;
    if (API_BASE && url.origin === new URL(API_BASE, window.location.origin).origin) return true;
  } catch {
    return true;
  }
  return false;
}

function isBackendPath(pathname, includeLegacy = true) {
  return pathname === "/api"
    || pathname.startsWith("/api/")
    || (includeLegacy && (pathname === "/opciones" || pathname.startsWith("/opciones/")));
}

export function resolveBackendUrl(input, { includeLegacy = true, includeTenantQuery = false } = {}) {
  if (!API_BASE || typeof window === "undefined") return input;
  if (typeof Request !== "undefined" && input instanceof Request) return input;

  const rawUrl = typeof input === "string" || input instanceof URL ? String(input) : "";
  if (!rawUrl) return input;

  try {
    const current = new URL(rawUrl, window.location.origin);
    if (!isBackendPath(current.pathname, includeLegacy)) return input;

    const apiOrigin = new URL(API_BASE, window.location.origin).origin;
    if (current.origin !== window.location.origin && current.origin !== apiOrigin) return input;
    const path = API_BASE.endsWith("/api") && current.pathname.startsWith("/api")
      ? current.pathname.slice(4) || "/"
      : current.pathname;
    const resolved = new URL(`${API_BASE}${path}${current.search}${current.hash}`);
    if (includeTenantQuery && !resolved.searchParams.has("tenant")) {
      resolved.searchParams.set("tenant", getTenantSlug());
    }
    return resolved.toString();
  } catch {
    return input;
  }
}

function mergeHeaders(existing) {
  const headers = new Headers(existing || undefined);
  const extra = tenantHeaders();
  for (const [key, value] of Object.entries(extra)) {
    if (!headers.has(key)) headers.set(key, value);
  }
  return headers;
}

export function installTenantRequestContext() {
  if (typeof window === "undefined") return;

  if (!window.__miAppTenantFetchInstalled && typeof window.fetch === "function") {
    const nativeFetch = window.fetch.bind(window);
    window.fetch = (input, init = {}) => {
      const resolvedInput = resolveBackendUrl(input);
      if (!shouldAttachTenant(resolvedInput)) return nativeFetch(resolvedInput, init);

      if (typeof Request !== "undefined" && resolvedInput instanceof Request) {
        const headers = mergeHeaders(init.headers || resolvedInput.headers);
        return nativeFetch(new Request(resolvedInput, { ...init, headers, credentials: init.credentials || "include" }));
      }

      return nativeFetch(resolvedInput, { ...init, credentials: init.credentials || "include", headers: mergeHeaders(init.headers) });
    };
    window.__miAppTenantFetchInstalled = true;
  }

  if (!window.__miAppBackendOpenInstalled && typeof window.open === "function") {
    const nativeOpen = window.open.bind(window);
    window.open = (url, target, features) => nativeOpen(resolveBackendUrl(url, {
      includeLegacy: false,
      includeTenantQuery: true,
    }), target, features);
    window.__miAppBackendOpenInstalled = true;
  }

  const headers = tenantHeaders();
  if (API_BASE) axios.defaults.baseURL = API_BASE;
  axios.defaults.headers.common["x-tenant"] = headers["x-tenant"];
  axios.defaults.headers.common["x-tenant-slug"] = headers["x-tenant-slug"];
  axios.defaults.withCredentials = true;

  if (!axios.__miAppTenantInterceptorInstalled) {
    axios.interceptors.request.use((config) => {
      const nextHeaders = config.headers || {};
      const current = tenantHeaders();
      nextHeaders["x-tenant"] = nextHeaders["x-tenant"] || current["x-tenant"];
      nextHeaders["x-tenant-slug"] = nextHeaders["x-tenant-slug"] || current["x-tenant-slug"];
      config.headers = nextHeaders;
      return config;
    });
    axios.__miAppTenantInterceptorInstalled = true;
  }
}
